'use strict';
import React, { useCallback, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	Typography,
	Box,
	IconButton,
	TextField,
	InputAdornment
} from '@mui/material';
import { Close, Search } from '@mui/icons-material';
import { CollapsibleList } from '@/components/index.jsx';
import { useProjectsQuery as useDiscoveryQuery } from '@/redux/services/discovery/projects.api';
import { useHistory } from 'react-router-dom';
import { keyBy } from 'lodash';
import { getProjectItems } from './myprojects/MyProjects.jsx';
import { Brand } from '@/lenses/uilenses/theme/colors';
import { usePendo } from '@/hooks/usePendo';

const getProjectAccountId = (project) => (
	project.publishDate ? project.projectCode : project.projectId
);

const TOOL_LABELS = {
	'/results': 'Download Results',
	'/pathwayexplorer': 'Pathway Explorer',
	'/statstable': 'Statistics Table',
};

export const ProjectsModal = ({ open, onClose, projects = [], onProjectSelect, selectMode, toolPath }) => {
	const history = useHistory();
	const { identify, pageLoad, initialized } = usePendo();
	const [searchText, setSearchText] = React.useState('');
	const { data: discoveryPanels } = useDiscoveryQuery(projects?.map(p => p.projectId), { skip: !projects });

	useEffect(() => {
		if (open && initialized) {
			pageLoad();
		}
	}, [open, initialized, pageLoad]);

	const filteredProjects = React.useMemo(() => {
		if (!searchText.trim()) return projects;
		const searchLower = searchText.toLowerCase();
		return projects.filter(p => 
			p.projectCode?.toLowerCase().includes(searchLower) ||
			p.projectTitle?.toLowerCase().includes(searchLower) ||
			p.customerProjectTitle?.toLowerCase().includes(searchLower)
		);
	}, [projects, searchText]);

	const handleProjectSelect = useCallback((project) => {
		const id = getProjectAccountId(project);
		if (id) {
			identify(id);
			pageLoad();
		}

		if (selectMode && onProjectSelect) {
			onProjectSelect(project);
		} else {
			history.push(`/home/project/${id}/home`);
			onClose();
		}
	}, [selectMode, onProjectSelect, history, onClose, identify, pageLoad]);

	const projectItems = useCallback(() => {
		if (!filteredProjects || !Array.isArray(filteredProjects)) return [];
		return getProjectItems(
			[...filteredProjects], 
			keyBy(discoveryPanels, 'projectId'), 
			null,
			handleProjectSelect
		);
	}, [filteredProjects, discoveryPanels, handleProjectSelect]);

	const toolLabel = toolPath ? TOOL_LABELS[toolPath] || 'Tool' : null;

	return (
		<Dialog 
			open={open} 
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: 3,
					boxShadow: '0 8px 32px rgba(0, 29, 56, 0.2)',
					maxHeight: '80vh',
					display: 'flex',
					flexDirection: 'column'
				}
			}}
		>
		<DialogTitle sx={{ 
			background: `linear-gradient(135deg, ${Brand.molecularBlue} 0%, ${Brand.livingPurple} 100%)`,
			color: Brand.pureWhite,
			display: 'flex',
			justifyContent: 'space-between',
			alignItems: 'center',
			padding: '20px 24px'
		}}>
			<Box>
				<Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
					{selectMode 
						? (toolLabel ? `Select Project to be used with ${toolLabel}` : 'Select a Project')
						: 'My Projects'
					}
				</Typography>
			</Box>
			<IconButton
				onClick={onClose}
				sx={{ color: Brand.pureWhite }}
				size="small"
			>
				<Close />
			</IconButton>
		</DialogTitle>
			<DialogContent sx={{ padding: '24px', flex: 1, overflow: 'auto' }}>
				<TextField
					fullWidth
					placeholder="Search projects..."
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					sx={{ mb: 3 }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search sx={{ color: Brand.molecularBlue }} />
							</InputAdornment>
						),
					}}
				/>
				
			{filteredProjects.length > 0 ? (
				<Box sx={{ 
					background: Brand.metaboliteWhite,
					borderRadius: 2,
					padding: 2,
					maxHeight: '50vh',
					overflow: 'auto'
				}}>
					<CollapsibleList 
						className="my-projects-list" 
						itemsToShow={10} 
						incrementAmount={10} 
						incrementShow={true}
					>
						{projectItems()}
					</CollapsibleList>
				</Box>
			) : (
					<Box sx={{ textAlign: 'center', py: 6 }}>
						<Typography variant="body1" sx={{ color: Brand.molecularBlue, mb: 2 }}>
							{searchText ? 'No projects found matching your search.' : 'No projects yet.'}
						</Typography>
						{searchText ? (
							<Button
								variant="outlined"
								onClick={() => setSearchText('')}
								sx={{
									borderColor: Brand.molecularBlue,
									color: Brand.molecularBlue,
									'&:hover': {
										borderColor: Brand.activatedRed,
										background: `${Brand.activatedRed}10`,
									}
								}}
							>
								Clear Search
							</Button>
						) : (
							<Box>
								<Typography variant="body2" sx={{ color: Brand.molecularBlue, mb: 3, maxWidth: 400, mx: 'auto' }}>
									If you'd like a quote for our services, more information on sample types and handling procedures, or simply have questions about how metabolomics can advance your research, please contact us.
								</Typography>
								<Button
									variant="contained"
									component="a"
									href="mailto:sales@metabolon.com?subject=Metabolync Portal Demo"
									sx={{
										background: Brand.activatedRed,
										'&:hover': {
											background: Brand.insightfulOrange,
										}
									}}
								>
									Contact Sales
								</Button>
							</Box>
						)}
					</Box>
				)}
			</DialogContent>
			<DialogActions sx={{ padding: '16px 24px', borderTop: `1px solid ${Brand.precisionGrey}` }}>
				<Button onClick={onClose} sx={{ color: Brand.molecularBlue }}>
					Close
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default ProjectsModal;

